import React, { useState, useEffect } from 'react';
import './Contact.css';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaFacebookF, FaTwitter, FaLinkedinIn } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const CONTACT_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE_URL}/contact#webpage`,
    "url": `${SITE_URL}/contact`,
    "name": "Contact Nimai Solutions | Free Web & App Development Consultation",
    "description": "Get in touch with Nimai Solutions for web development, mobile app development, and UI/UX design. Free consultation available for businesses in Vadodara and across India.",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home",    "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${SITE_URL}/contact/` }
        ]
    }
};

const Contact = () => {
    useSEO({
        title:       "Contact Nimai Solutions | Free Web & App Development Consultation",
        description: "Contact Nimai Solutions in Vadodara for a free consultation on web development, mobile apps, Flutter development, and UI/UX design. We respond within 24 hours.",
        canonical:   `${SITE_URL}/contact`,
        keywords:    "contact Nimai Solutions, web development consultation Vadodara, hire app developer Gujarat, free website quote India",
        schema:      CONTACT_SCHEMA,
    });

    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [socialLinks, setSocialLinks] = useState(null);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [contentResult, socialsResult, servicesResult] = await Promise.all([
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/contact-pages'),
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/social-links'),
                    safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/services?t=${Date.now()}`)
                ]);

                if (contentResult.ok) {
                    let data = null;
                    if (Array.isArray(contentResult.data)) data = contentResult.data[0];
                    else if (contentResult.data.data) data = Array.isArray(contentResult.data.data) ? contentResult.data.data[0] : contentResult.data.data;
                    setContent(data);
                }

                if (socialsResult.ok && socialsResult.data.data) {
                    setSocialLinks(socialsResult.data.data);
                }

                if (servicesResult.ok) {
                    const servicesData = servicesResult.data.data || servicesResult.data;
                    setServices(Array.isArray(servicesData) ? servicesData : []);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    if (isLoading) {
        return (
            <div className="contact-loading">
                <div className="loader"></div>
            </div>
        );
    }

    // If no content exists at all, show Coming Soon
    if (!content && !socialLinks) {
        return (
            <div className="coming-soon-container" style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'var(--primary-blue)',
                color: 'white',
                textAlign: 'center',
                padding: '20px'
            }}>
                <Navbar />
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ fontSize: '4rem', fontWeight: '800', marginBottom: '20px' }}
                >
                    Coming <span style={{ color: 'var(--accent-orange)' }}>Soon</span>
                </motion.h1>
                <p style={{ fontSize: '1.2rem', opacity: 0.8, maxWidth: '600px' }}>
                    We're currently refining our contact experience. Please check back shortly or reach out to us via our official channels.
                </p>
                <button
                    onClick={() => navigate('/')}
                    style={{
                        marginTop: '40px',
                        padding: '15px 40px',
                        borderRadius: '50px',
                        border: 'none',
                        background: 'var(--accent-orange)',
                        color: 'white',
                        fontWeight: '700',
                        cursor: 'pointer'
                    }}
                >
                    Return Home
                </button>
            </div>
        );
    }

    const displayData = content || {
        page_title: "Let's Connect",
        page_description: "Fill out the form below and our strategy team will reach out within 24 hours to schedule your session.",
        email_address: "hello@nimaisolutions.com",
        phone_number: "+1 (555) 000-0000",
        physical_address: "123 Tech District, Suite 400, San Francisco, CA 94103",
        form_title: "Get a Free Consultation",
        submit_button_text: "Book My Free Strategy Call",
        form_footer_note: "No obligation. Free 30-minute strategy call with an expert.",
        social_proof_title: "Trusted by Global Innovators"
    };

    const displaySocials = socialLinks || {
        facebook: '#',
        twitter: '#',
        linkedin: '#'
    };

    return (
        <div className="contact-container">
            <Navbar />

            <div className="contact-header">
                <motion.h1
                    className="contact-title"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {displayData.page_title}
                </motion.h1>
                <motion.p
                    className="contact-subtitle"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {displayData.page_description}
                </motion.p>
            </div>

            <motion.div
                className="contact-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
            >
                {/* Left Side */}
                <div className="contact-info-panel">
                    <div className="contact-info-header">
                        <h3>Contact Information</h3>
                        <p style={{ opacity: 0.8, fontSize: '0.9rem' }}>Fill up the form or reach us directly.</p>
                    </div>

                    <div className="info-items">
                        <div className="info-item">
                            <div className="info-icon"><FaEnvelope /></div>
                            <div className="info-content">
                                <h4>Email Us</h4>
                                <p>{displayData.email_address}</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon"><FaPhoneAlt /></div>
                            <div className="info-content">
                                <h4>Call Us</h4>
                                <p>{displayData.phone_number}</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon"><FaMapMarkerAlt /></div>
                            <div className="info-content">
                                <h4>Visit Our Office</h4>
                                <p dangerouslySetInnerHTML={{ __html: displayData.physical_address.replace(/\n/g, '<br />') }}></p>
                            </div>
                        </div>
                    </div>

                    <div className="social-follow">
                        <p>FOLLOW US</p>
                        <div className="contact-socials">
                            <a href={displaySocials.facebook} target="_blank" rel="noopener noreferrer" className="social-btn"><FaFacebookF /></a>
                            <a href={displaySocials.twitter} target="_blank" rel="noopener noreferrer" className="social-btn"><FaTwitter /></a>
                            <a href={displaySocials.linkedin} target="_blank" rel="noopener noreferrer" className="social-btn"><FaLinkedinIn /></a>
                        </div>
                    </div>

                    {/* Decorative Circle */}
                    <div style={{
                        position: 'absolute',
                        bottom: '-100px',
                        right: '-100px',
                        width: '250px',
                        height: '250px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.05)'
                    }}></div>
                </div>

                {/* Right Side - Form */}
                <div className="contact-form-panel">
                    <div className="form-header">
                        <h3>{displayData.form_title}</h3>
                    </div>

                    <form className="contact-form" onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = {
                            full_name: e.target.elements[0].value,
                            email: e.target.elements[1].value,
                            company_name: e.target.elements[2].value,
                            project_type: e.target.elements[3].value,
                            message: e.target.elements[4].value
                        };

                        if (!formData.full_name || !formData.email || !formData.message) {
                            toast.error('Please fill in all required fields.');
                            return;
                        }

                        const btn = e.target.querySelector('.submit-btn');
                        const originalText = btn.innerText;
                        btn.innerText = 'Submitting...';
                        btn.disabled = true;

                        try {
                            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/consultations', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Accept': 'application/json'
                                },
                                body: JSON.stringify(formData)
                            });

                            if (ok) {
                                toast.success(result.message || 'Consultation request submitted successfully!');
                                e.target.reset();
                            } else {
                                toast.error(result.message || 'Failed to submit request. Please try again.');
                            }
                        } catch (error) {
                            console.error('Submission error:', error);
                            toast.error('Network error. Please try again later.');
                        } finally {
                            btn.innerText = originalText;
                            btn.disabled = false;
                        }
                    }}>
                        <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input type="text" className="form-input" placeholder="John Doe" required />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Work Email</label>
                            <input type="email" className="form-input" placeholder="john@company.com" required />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Company Name</label>
                            <input type="text" className="form-input" placeholder="Your Firm Inc." />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Project Type</label>
                            <select className="form-select" required>
                                <option value="">Select an option</option>
                                {services.length > 0 ? (
                                    services.map(service => (
                                        <option key={service.id} value={service.title}>{service.title}</option>
                                    ))
                                ) : (
                                    <>
                                        <option value="Web Development">Web Development</option>
                                        <option value="Mobile App">Mobile App</option>
                                        <option value="Cloud Infrastructure">Cloud Infrastructure</option>
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label className="form-label">Message</label>
                            <textarea className="form-textarea" placeholder="Tell us about your goals and current challenges..." required></textarea>
                        </div>

                        <div className="form-group full-width">
                            <button type="submit" className="submit-btn">{displayData.submit_button_text}</button>
                            <span className="form-note">{displayData.form_footer_note}</span>
                        </div>
                    </form>
                </div>
            </motion.div>

            <div className="trusted-section">
                <h4 className="trusted-title">{displayData.social_proof_title}</h4>
                <div className="logos-grid">
                    <div className="client-logo"></div>
                    <div className="client-logo"></div>
                    <div className="client-logo"></div>
                    <div className="client-logo"></div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Contact;
